import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { _fetchFieldData } from "@/services/clientCommon";
import {
  _fetchClientContactProfile,
  _fetchClientProfile,
  _getSecurityCode,
  _updateClientContactProfile,
  _updateProfile,
} from "@/services/clientprofile";
import { handleAPICatch, showSuccessToast } from "@/utilities/utils";

import { setSession } from "./session";

const initialState = {
  user: {
    id: null,
    type: null,
    email: null,
    token: null,
    role_name: null,
    firstname: null,
    lastname: null,
    is_headoffice: null,
    original_user: null,
    client_manager_name: null,
    client_practice_code: null,
    client_allow_publish_job: 0,
    is_primary_contact: 0,
  },
  isLoading: false,
  isContactLoading: false,
  profileData: {},
  contactProfileData: {},
  user_title: [],
};

export const getSecurityCode = createAsyncThunk(
  "clientProfile/_getSecurityCode",
  async (data, { dispatch }) => {
    const response = _getSecurityCode(data);
    response
      .then((res) => {
        const {
          data: { code, message },
        } = res;
        showSuccessToast(message);
        dispatch(updateSecurityCode(code));
      })
      .catch((error) => {
        handleAPICatch(error);
      });
  },
);

export const fetchFields = createAsyncThunk(
  "clientCreateJob/fetchFields",
  async (data, { dispatch }) => {
    let _key = data.key ? data.key : null;
    if (_key) {
      delete data.key;
    }
    const response = _fetchFieldData(data);
    response
      .then((res) => {
        const {
          data: { field_data },
        } = res;
        let _profileData = {
          [_key]: field_data,
        };
        dispatch(setProfileData(_profileData));
      })
      .catch((error) => {
        handleAPICatch(error);
      });
    return response;
  },
);

export const fetchClientProfile = createAsyncThunk(
  "clientProfile/fetchClientProfile",
  async (data, { dispatch }) => {
    dispatch(setUserLoading(true));
    const response = _fetchClientProfile();
    response
      .then((res) => {
        const { data } = res;
        let _profileData = {
          profile: data,
        };
        const normalFields = [
          {
            key: "all_region",
            type: "all_region",
          },
          {
            key: "all_sector",
            type: "all_sector",
          },
          {
            key: "all_ccg_group",
            type: "all_ccg_group",
          },
          {
            key: "all_it_system",
            type: "all_it_system",
          },
          {
            key: "all_counties",
            type: "all_county",
          },
          {
            key: "region_from_county",
            type: "all_regions_from_county",
          },
        ];
        dispatch(setProfileData(_profileData));
        dispatch(
          fetchFields({
            field_type: "all_city",
            id: data.region,
            key: "all_cities",
          }),
        );
        normalFields.map((field) => {
          dispatch(
            fetchFields({
              field_type: field.type,
              key: field.key,
            }),
          );
        });
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        dispatch(setUserLoading(false));
      });
  },
);

export const fetchClientContactProfile = createAsyncThunk(
  "clientProfile/fetchClientContactProfile",
  async (data, { dispatch }) => {
    dispatch(setClientContactLoading(true));
    const response = _fetchClientContactProfile();
    response
      .then((res) => {
        const {
          data: { client_user_data, client_user_title },
        } = res;
        dispatch(setContactProfileData(client_user_data));
        dispatch(updateUserTitle(client_user_title));
      })
      .catch((error) => {
        // handleAPICatch(error);
      })
      .finally(() => {
        dispatch(setClientContactLoading(false));
      });
  },
);

export const updateClientProfileData = createAsyncThunk(
  "clientprofile/updateClientProfileData",
  async ({ values, setSubmitting, addList, setAddList }, { dispatch }) => {
    const response = _updateProfile(values);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        showSuccessToast(message);
        setAddList([]);
        dispatch(fetchClientProfile());
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
);

export const updateClientContactProfileData = createAsyncThunk(
  "clientprofile/updateClientContactProfileData",
  async (
    { values, setSubmitting, contactProfileObj },
    { dispatch, getState },
  ) => {
    setSubmitting(true);
    const _values = {
      client_user_id: contactProfileObj?.id,
      contact_title: values.contact_title,
      contact_forename: values.contact_forename,
      contact_surname: values.contact_surname,
      contact_mobile: values.contact_mobile,
      contact_telno: values.contact_telno,
    };
    const response = _updateClientContactProfile(_values);
    response
      .then((res) => {
        const {
          data: { message },
        } = res;
        showSuccessToast(message);
        dispatch(fetchClientContactProfile());
        dispatch(
          updateUser({
            firstname: values.contact_forename,
            lastname: values.contact_surname,
          }),
        );
        dispatch(
          setSession({
            firstname: values.contact_forename,
            lastname: values.contact_surname,
          }),
        );
      })
      .catch((error) => {
        handleAPICatch(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
);

const clientProfileSlice = createSlice({
  name: "clientprofile",
  initialState,
  reducers: {
    /**
     * Set the user in the state.
     *
     * @param {Object} state - the current state
     * @param {Object} action - the action containing the payload
     * @return {void}
     */
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUserLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setClientContactLoading: (state, action) => {
      state.isContactLoading = action.payload;
    },
    /**
     * Updates the user in the state with the provided action payload.
     *
     * @param {Object} state - the current state
     * @param {Object} action - the action containing the payload for updating the user
     */
    updateUser: (state, action) => {
      state.user = {
        ...state.user,
        ...action.payload,
      };
    },
    /**
     * Sets the profile data in the given state.
     *
     * @param {type} state - the state object
     * @param {type} action - the action object containing the payload
     * @return {type} undefined
     */
    setProfileData: (state, action) => {
      state.profileData = {
        ...state.profileData,
        ...action.payload,
      };
    },
    setContactProfileData: (state, action) => {
      state.contactProfileData = {
        ...state.contactProfileData,
        ...action.payload,
      };
    },
    /**
     * Update security code in the state.
     *
     * @param {type} state - description of parameter
     * @param {type} action - description of parameter
     * @return {type} description of return value
     */
    updateSecurityCode: (state, action) => {
      var sCode = action.payload;
      var tempProfileData = state.profileData;
      tempProfileData.profile.security_code = sCode;
      state.profileData = tempProfileData;
    },
    updateUserTitle: (state, action) => {
      state.user_title = action.payload;
    },
  },
  /**
   * A function that adds extra reducers for handling different cases of fetchClientInvoices.
   *
   * @param {object} builder - The reducer builder object
   * @return {void}
   */
});

export const {
  setUser,
  updateUser,
  setProfileData,
  setContactProfileData,
  setUserLoading,
  setClientContactLoading,
  updateSecurityCode,
  updateUserTitle,
} = clientProfileSlice.actions;

export default clientProfileSlice.reducer;
